/**
 * Address lookup, through the API rather than through Google directly.
 *
 * The browser previously called `google.maps.places` itself, which meant the
 * Maps browser key was doing autocomplete as well as drawing maps, billed per
 * keystroke against a key that is public by construction. Routing the lookup
 * through the API keeps the Places key server-side, lets the API cache repeated
 * queries, and gives the web client the same suggestions the Flutter clients
 * get rather than a second, subtly different list.
 *
 * Session tokens are the part worth getting right. Google bills autocomplete by
 * session: every keystroke in one lookup plus the details call that resolves it
 * count once, but only if they all carry the same token. `useAutocomplete`
 * below holds a token for the life of a lookup and clears it when a suggestion
 * is chosen, which is exactly the boundary Google means by a session.
 */

import { useCallback, useRef, useState } from "react";

import { api } from "../api";
import type {
    AutocompleteArgs,
    AutocompleteResult,
    Place,
    PlaceDetailsArgs,
    Point,
    ReverseGeocodeArgs,
} from "../types";

export const placesApi = api.injectEndpoints({
    endpoints: (build) => ({
        /**
         * Suggestions for a partial address.
         *
         * Deliberately untagged. These are not a resource that anything else
         * invalidates: they are a function of the text typed, and the API
         * already caches them. `keepUnusedDataFor` is short because a
         * suggestion list held after the user has moved on is only a memory
         * cost.
         */
        autocomplete: build.query<AutocompleteResult, AutocompleteArgs>({
            query: ({ query, ...rest }) => ({
                url: "places/autocomplete",
                params: {
                    query,
                    ...(rest.sessionToken
                        ? { sessionToken: rest.sessionToken }
                        : {}),
                    // Both or neither: the API reads them as a pair and a lone
                    // longitude biases nothing.
                    ...(rest.longitude !== undefined &&
                    rest.latitude !== undefined
                        ? {
                              longitude: rest.longitude,
                              latitude: rest.latitude,
                          }
                        : {}),
                    ...(rest.radiusMeters
                        ? { radiusMeters: rest.radiusMeters }
                        : {}),
                    ...(rest.countries ? { countries: rest.countries } : {}),
                    ...(rest.language ? { language: rest.language } : {}),
                },
            }),
            keepUnusedDataFor: 30,
        }),

        /**
         * Resolves one suggestion to a coordinate.
         *
         * Pass the same `sessionToken` the autocomplete calls used. Omitting it
         * does not fail, it just bills the details call separately from the
         * keystrokes that led to it.
         */
        placeDetails: build.query<Place, PlaceDetailsArgs>({
            query: ({ placeId, sessionToken, language }) => ({
                url: "places/details",
                params: {
                    placeId,
                    ...(sessionToken ? { sessionToken } : {}),
                    ...(language ? { language } : {}),
                },
            }),
            // A place's coordinates do not change, so this is worth holding for
            // longer than the suggestions that led to it.
            keepUnusedDataFor: 300,
        }),

        /**
         * Names the point the browser reported. This is "use my current
         * location".
         *
         * The coordinate that comes back is the coordinate that went out: the
         * API does not snap it to the building it matched, because a pick-up
         * moved to the nearest landmark is on the wrong side of the road. Only
         * the address is new information.
         *
         * No session token. This is not part of an autocomplete session and
         * Google does not bill it as one.
         */
        reverseGeocode: build.query<Place, ReverseGeocodeArgs>({
            query: ({ latitude, longitude, language }) => ({
                url: "places/reverse",
                params: {
                    latitude,
                    longitude,
                    ...(language ? { language } : {}),
                },
            }),
            keepUnusedDataFor: 60,
        }),
    }),
});

export const {
    useAutocompleteQuery,
    useLazyAutocompleteQuery,
    usePlaceDetailsQuery,
    useLazyPlaceDetailsQuery,
    useReverseGeocodeQuery,
    useLazyReverseGeocodeQuery,
} = placesApi;

/** How long to wait after the last keystroke before asking the API. */
const DEBOUNCE_MS = 250;

/** Below this, suggestions are noise and the request is not worth making. */
const MIN_QUERY_LENGTH = 3;

export interface UseAutocompleteOptions {
    /** Biases results towards the user's position, when it is known. */
    origin?: Point;
    /** ISO country codes, comma separated. */
    countries?: string;
}

/**
 * Address search for a text input.
 *
 * Owns three things a component should not have to: the debounce, the Google
 * billing session token, and the resolve step that turns the chosen suggestion
 * into a coordinate. `resolve` returns the full place, so a caller can store
 * the address and the point together in one await.
 */
export function useAutocomplete(options: UseAutocompleteOptions = {}) {
    const [trigger, state] = useLazyAutocompleteQuery();
    const [resolveDetails] = useLazyPlaceDetailsQuery();

    // A ref, not state: changing the token must not re-render, and the pending
    // timer has to survive re-renders to be cancellable.
    const sessionToken = useRef<string | undefined>(undefined);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const [query, setQuery] = useState("");

    const search = useCallback(
        (text: string) => {
            setQuery(text);
            if (timer.current) clearTimeout(timer.current);

            if (text.trim().length < MIN_QUERY_LENGTH) return;

            timer.current = setTimeout(() => {
                void trigger({
                    query: text.trim(),
                    sessionToken: sessionToken.current,
                    longitude: options.origin?.longitude,
                    latitude: options.origin?.latitude,
                    countries: options.countries,
                })
                    .unwrap()
                    .then((result) => {
                        // The API generates a token when we send none; holding
                        // the one it echoes is what keeps the rest of this
                        // lookup inside a single billed session.
                        sessionToken.current ??= result.sessionToken;
                    })
                    .catch(() => {
                        // Swallowed deliberately: the error is already on
                        // `state` for the caller to render, and an unhandled
                        // rejection here would be noise in the console on every
                        // dropped keystroke.
                    });
            }, DEBOUNCE_MS);
        },
        [trigger, options.origin, options.countries]
    );

    /**
     * Turns a chosen suggestion into a place, and closes the billing session.
     *
     * The token is cleared whatever happens: a details call that failed still
     * ended the session as far as Google is concerned, and reusing the token
     * afterwards would bill the next lookup into a session that is already
     * closed.
     */
    const resolve = useCallback(
        async (placeId: string): Promise<Place> => {
            const token = sessionToken.current;
            sessionToken.current = undefined;
            return await resolveDetails({ placeId, sessionToken: token }).unwrap();
        },
        [resolveDetails]
    );

    /** Abandons the lookup: clears the input, the timer and the session. */
    const reset = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        sessionToken.current = undefined;
        setQuery("");
    }, []);

    return {
        query,
        search,
        resolve,
        reset,
        suggestions: state.data?.suggestions ?? [],
        isFetching: state.isFetching,
        error: state.error,
    };
}
